import React from 'react'
import styled from '@emotion/styled'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { Icon, IconType } from '@mobi/component-library/Legacy/Icon'
import { CountdownBadge } from '@mobi/component-library/Feedback/CountdownBadge'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import type { MetaDataHighlight } from '@core/Areas/Search/types'
import type { RaceCodes } from '@core/Areas/Racing/Types/MeetingInformation'
import { HighlightDisplay } from '@core/Areas/Search/Components/Common/HighlightDisplay'

interface Props {
  locationName: string
  raceNumber?: number
  code?: RaceCodes
  advertisedStartTime?: string
  status?: string
  highlightKey: string
  highlights: MetaDataHighlight
  scratchedStatus?: string
}

export const LocationTime: React.FC<Props> = ({
  locationName,
  raceNumber,
  code,
  advertisedStartTime,
  status,
  highlightKey,
  highlights,
  scratchedStatus,
}) => {
  const resultStatus = scratchedStatus || status
  const codeIcon = code ? IconTypes[code] : undefined

  return (
    <Flex alignItems='center' gap={spacing.smx1}>
      {resultStatus && (
        <StatusBadge color='red' badgeSize='S'>
          {resultStatus}
        </StatusBadge>
      )}

      {advertisedStartTime && (
        <CountdownBadge advertisedStartTime={new Date(advertisedStartTime)} badgeSize='S' />
      )}

      <LocationNameStyled data-tid-search-location={locationName}>
        <HighlightDisplay
          displayText={locationName}
          highlightKey={highlightKey}
          highlights={highlights}
        />

        {raceNumber && <span>{`R${raceNumber}`}</span>}

        {codeIcon && <Icon type={codeIcon} size='2.2rem' />}
      </LocationNameStyled>
    </Flex>
  )
}

// =============
// Local Helpers
// =============

const IconTypes: Record<string, IconType> = {
  Races: 'races',
  Trots: 'trot',
  Dogs: 'dog',
}

// ======
// Styles
// ======

const LocationNameStyled = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: spacing.smx2,
  marginLeft: 'auto',
  fontSize: font.size.md.fontSize,
  letterSpacing: font.size.md.letterSpacing,
  lineHeight: font.size.md.lineHeight,
  fontWeight: font.weight.regular,
  color: colors.neutral[800],

  '@media (max-width: 350px)': {
    maxWidth: '20rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
})
